/**
 * Leer un calendario `.ics`.
 *
 * Existe porque la Junta publica el calendario escolar en este formato, pero lo
 * que lo hace la solución buena no es eso: es que `.ics` es un estándar. Sirve
 * el de la Junta, el que mande el colegio, y uno exportado de cualquier
 * calendario. El día que cambie la web de la consejería, o que esta familia se
 * mude de comunidad, esto sigue funcionando igual.
 *
 * Y se lee **en la tablet**. El fichero no sube a ningún sitio: es la única de
 * las opciones que se barajaron que no añade una dependencia de red a una app
 * que vive en modo avión.
 *
 * Sin dependencias, porque no hacen falta: es un formato de líneas. Lo que sí
 * hace falta es respetar tres detalles que son de donde salen todos los errores
 * al leer un `.ics` a mano, y los tres están abajo.
 */

export interface IcsRange {
  readonly summary: string
  /** Primer día, «YYYY-MM-DD». */
  readonly from: string
  /** Último día, inclusive. Ojo: en el fichero `DTEND` es exclusivo. */
  readonly to: string
}

/**
 * Detalle 1: el plegado de líneas.
 *
 * La especificación parte las líneas largas y continúa con un espacio o un
 * tabulador al principio de la siguiente. Un resumen como «Vacaciones de
 * Navidad» puede llegar partido en dos, y leer línea a línea daría «Vacaciones
 * de Navi» y una línea suelta con «dad».
 */
function desplegar(texto: string): string[] {
  const lineas: string[] = []
  for (const cruda of texto.split(/\r\n|\n|\r/)) {
    const ultima = lineas.at(-1)
    if ((cruda.startsWith(' ') || cruda.startsWith('\t')) && ultima !== undefined) {
      lineas[lineas.length - 1] = ultima + cruda.slice(1)
    } else {
      lineas.push(cruda)
    }
  }
  return lineas
}

/** Detalle 2: el texto va con escapes propios, no los de JSON. */
function desescapar(valor: string): string {
  return valor
    .replace(/\\n/gi, ' ')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\')
    .trim()
}

/** «20261220» o «20261220T090000Z» a «2026-12-20». */
function aFecha(valor: string): string | null {
  const match = /^(\d{4})(\d{2})(\d{2})/.exec(valor.trim())
  if (!match) return null
  return `${match[1]}-${match[2]}-${match[3]}`
}

/** Un día antes, en civil puro, sin pasar por `Date`. */
function diaAnterior(fecha: string): string {
  const [y, m, d] = fecha.split('-').map(Number)
  if (y === undefined || m === undefined || d === undefined) return fecha
  const dias = Date.UTC(y, m - 1, d) / 86_400_000 - 1
  const atras = new Date(dias * 86_400_000)
  return atras.toISOString().slice(0, 10)
}

/**
 * Quita del nombre el código y el centro que la Junta pega al final.
 *
 * «Vacaciones de Navidad - Fiesta local - 29009144-C.E.I.P. María Zambrano» es
 * el nombre de un tramo con el colegio detrás. Quien revisa la importación ya
 * sabe de qué colegio es.
 */
function limpiarNombre(valor: string): string {
  return valor.replace(/\s*-\s*\d{6,8}-.*$/, '').trim()
}

/**
 * Saca los rangos de un `.ics`. Nunca lanza: un fichero que no se entiende es
 * una lista vacía, porque lo que hay al otro lado es un padre que se ha
 * equivocado de fichero, no un error que arreglar.
 */
export function parseIcs(texto: string): readonly IcsRange[] {
  const rangos: IcsRange[] = []
  let dentro = false
  let summary = ''
  let descripcion = ''
  let desde: string | null = null
  let hasta: string | null = null
  let hastaEsExclusivo = false

  for (const linea of desplegar(texto)) {
    const limpia = linea.trim()

    if (limpia.toUpperCase() === 'BEGIN:VEVENT') {
      dentro = true
      summary = ''
      descripcion = ''
      desde = null
      hasta = null
      hastaEsExclusivo = false
      continue
    }

    if (limpia.toUpperCase() === 'END:VEVENT') {
      if (dentro && desde !== null) {
        /**
         * Detalle 3, y el que más se falla: en un evento de día completo,
         * `DTEND` es **exclusivo**. Unas vacaciones del 20 de diciembre al 7 de
         * enero se escriben con DTEND=20270108, y tomarlo tal cual metería el
         * día 8 dentro de las vacaciones — un día de colegio con presupuesto de
         * fin de semana, en silencio.
         */
        const ultimo = hasta === null ? desde : hastaEsExclusivo ? diaAnterior(hasta) : hasta
        const nombreBueno = descripcion.length > 0 ? descripcion : limpiarNombre(summary)
        rangos.push({
          summary: nombreBueno.length > 0 ? nombreBueno : 'Sin nombre',
          from: desde,
          to: ultimo < desde ? desde : ultimo,
        })
      }
      dentro = false
      continue
    }

    if (!dentro) continue

    const corte = limpia.indexOf(':')
    if (corte < 0) continue
    const nombre = limpia.slice(0, corte).toUpperCase()
    const valor = limpia.slice(corte + 1)

    if (nombre === 'SUMMARY' || nombre.startsWith('SUMMARY;')) {
      summary = desescapar(valor)
    } else if (nombre === 'DESCRIPTION' || nombre.startsWith('DESCRIPTION;')) {
      // El de la Junta repite el nombre en `SUMMARY` con el código y el nombre
      // del colegio pegados detrás —«... - 29009144-C.E.I.P. María Zambrano»— y
      // en `DESCRIPTION` lo deja limpio. Cuando hay descripción, esa gana.
      descripcion = desescapar(valor)
    } else if (nombre === 'DTSTART' || nombre.startsWith('DTSTART;')) {
      desde = aFecha(valor)
    } else if (nombre === 'DTEND' || nombre.startsWith('DTEND;')) {
      hasta = aFecha(valor)
      // Solo es exclusivo en los eventos de día completo. Uno con hora termina
      // cuando dice, y ese mismo día cuenta.
      hastaEsExclusivo = !valor.includes('T')
    }
  }

  return rangos
}

/**
 * Junta los días seguidos que se llaman igual.
 *
 * El calendario de la Junta no trae tramos: trae un evento por día. Unas
 * vacaciones de Semana Blanca son cuatro eventos idénticos del 22 al 25, y
 * revisar veintiséis filas para ocho decisiones es cansado y se revisa peor.
 *
 * Se unen solo los días **de verdad seguidos**, sin saltar fines de semana. Es
 * más tonto que mirar si el hueco es sábado y domingo, y es a propósito: lo que
 * se enseña es lo que el fichero dice, no lo que la app deduce.
 */
export function mergeRanges(rangos: readonly IcsRange[]): readonly IcsRange[] {
  const ordenados = [...rangos].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0))
  const unidos: IcsRange[] = []

  for (const rango of ordenados) {
    const ultimo = unidos.at(-1)
    if (ultimo?.summary === rango.summary && siguienteDia(ultimo.to) === rango.from) {
      unidos[unidos.length - 1] = { ...ultimo, to: rango.to }
      continue
    }
    unidos.push({ ...rango })
  }

  return unidos
}

/** Un día después, en civil puro. */
function siguienteDia(fecha: string): string {
  const [y, m, d] = fecha.split('-').map(Number)
  if (y === undefined || m === undefined || d === undefined) return fecha
  return new Date(Date.UTC(y, m - 1, d) + 86_400_000).toISOString().slice(0, 10)
}

/**
 * Qué clase de día parece cada tramo, a partir de su nombre.
 *
 * Va deliberadamente corta de manga. Un calendario escolar no solo trae
 * vacaciones: trae «inicio de curso», «reunión de tutoría», «entrega de notas»
 * — días de colegio con nombre propio. Marcar uno de esos como festivo le daría
 * al niño quince minutos más de tablet un día lectivo, **y nadie lo notaría**,
 * porque lo único que se ve por fuera es un número que nadie mira.
 *
 * Así que solo se pronuncia sobre lo que reconoce, y lo demás vuelve sin
 * clasificar para que lo decida un adulto. Equivocarse por defecto es que falte
 * un festivo, que se ve enseguida y se arregla en un toque.
 */
export type IcsGuess = 'vacation' | 'holiday' | undefined

const VACACIONES = /vacacion|navidad|semana santa|verano|blanca/i
const FESTIVO = /no lectiv|libre disposici|festiv|fiesta|d[ií]a de andaluc/i

export function guessKind(summary: string): IcsGuess {
  if (VACACIONES.test(summary)) return 'vacation'
  if (FESTIVO.test(summary)) return 'holiday'
  return undefined
}
